import React, { useState, useEffect, useRef } from 'react';
import { Search, X, ChevronDown, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../lib/api';
import { iskanjeKontov } from '../lib/kontoIskanje'; // [PATCH konto-iskanje-naziv v1]

function _barvaBesedeDok(s) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return { bg: `hsl(${h}, 75%, 90%)`, br: `hsl(${h}, 45%, 72%)` };
}

function _krajsajDok(s, n) { const t = String(s || ''); return t.length > n ? t.slice(0, n) + '…' : t; }

function PartnerPickerDok({ companyId, initialTax, initialLabel, onChange }: { companyId: string; initialTax?: string; initialLabel?: string; onChange: (tax: string, label: string) => void }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rez, setRez] = useState<any[]>([]);
  const [tax, setTax] = useState(initialTax || '');
  const [label, setLabel] = useState(initialLabel || '');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const qq = q.trim();
    if (qq.length < 2) { setRez([]); return; }
    const t = setTimeout(() => {
      api.get(`/sifranti/partnerji/${companyId}?limit=12&q=${encodeURIComponent(qq)}`)
        .then(r => setRez(r.data || [])).catch(() => setRez([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, companyId]);
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) { setOpen(false); setQ(''); } };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const izberi = (p: any) => {
    const t = p.tax_number || p.davcna || ''; const ime = p.name || p.naziv || '';
    setTax(t); setLabel(ime); setOpen(false); setQ(''); onChange(t, ime);
  };
  const pocisti = (e: React.MouseEvent) => { e.stopPropagation(); setTax(''); setLabel(''); setQ(''); onChange('', ''); };
  const prikaz = label ? _krajsajDok(label, 22) : (tax ? '@' + tax : 'Vsi (brez partnerja)');
  const prazen = !label && !tax;

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 border border-gray-700 rounded-lg bg-gray-800 text-sm text-white hover:border-blue-500 transition">
        <span className={prazen ? 'text-gray-400 truncate' : 'truncate'}>{prikaz}</span>
        <span className="flex items-center gap-1 flex-shrink-0">
          {!prazen && <span onClick={pocisti} title="Počisti" className="text-gray-400 hover:text-white"><X className="w-4 h-4" /></span>}
          <span className="text-gray-400 text-xs">▾</span>
        </span>
      </button>
      {open && (
        <div className="absolute left-0 right-0 top-11 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-[10001]">
          <div className="p-2 border-b border-gray-700">
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="išči partnerja po imenu…"
              className="w-full px-2 py-1.5 bg-gray-900 text-sm text-white rounded outline-none" />
          </div>
          <div className="max-h-44 overflow-y-auto">
            {rez.length === 0 ? (
              <div className="px-3 py-3 text-xs text-gray-500">{q.trim().length < 2 ? 'Vtipkaj vsaj 2 znaka…' : 'Ni zadetkov.'}</div>
            ) : rez.map((p: any) => (
              <button key={p.id || (p.tax_number || p.davcna)} onClick={() => izberi(p)}
                className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-gray-700 flex justify-between gap-2">
                <span className="truncate">{p.name || p.naziv}</span>
                <span className="font-mono text-xs text-gray-500">{p.tax_number || p.davcna}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function BesedaModalDok({ companyId, koncniKonti, initial, onClose, onSaved }: { companyId: string; koncniKonti: any[]; initial: any; onClose: () => void; onSaved: () => void }) {
  const [beseda, setBeseda] = useState(initial?.beseda || '');
  const [konto, setKonto] = useState(initial?.konto || '');
  const [partnerTax, setPartnerTax] = useState(initial?.partner_tax_number || '');
  const [kategorija, setKategorija] = useState(initial?.ddv_kategorija || 'dom_obdavcen'); // [PATCH ir4a-ui-kw v1]
  const jeIzdani = initial?.smer === 'izdani'; // [PATCH ir4a-ui-kw v1]
  const [vloga, setVloga] = useState(initial?.vloga || 'postavka'); // [PATCH besede-vloga-ui v1]
  const [saving, setSaving] = useState(false);
  const nazivKonta = (code: string) => { const k = (koncniKonti || []).find((x: any) => x.code === code); return k ? k.name : ''; };

  const shrani = async () => {
    if (!beseda.trim()) { toast.error('Vnesite besedo'); return; }
    if (!konto.trim()) { toast.error('Vnesite konto'); return; }
    setSaving(true);
    try {
      await api.put(`/sifranti/keywords/${companyId}/${initial.id}`, { beseda: beseda.trim(), konto: konto.trim(), partner_tax_number: (partnerTax || '').trim() || null, vloga, ...(jeIzdani ? { ddv_kategorija: kategorija } : {}) }); // [PATCH ir4a-ui-kw v1] + [PATCH besede-vloga-ui v1]
      onSaved();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); } finally { setSaving(false); }
  };
  const izbrisi = async () => {
    try { await api.delete(`/sifranti/keywords/${companyId}/${initial.id}`); onSaved(); }
    catch { toast.error('Napaka pri brisanju'); }
  };

  return (
    <div className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-md p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg text-white">Uredi opis</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Beseda</label>
            <input autoFocus value={beseda} onChange={e => setBeseda(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') shrani(); }}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white outline-none focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Konto (končni, ≥4 mesta)</label>
            <input value={konto} onChange={e => setKonto(e.target.value)} list="kw-konti-dok"
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white font-mono outline-none focus:border-blue-500" />
            <datalist id="kw-konti-dok">
              {koncniKonti.map((k: any) => <option key={k.code} value={k.code}>{k.code} — {k.name}</option>)}
            </datalist>
            {konto && nazivKonta(konto) && <p className="text-[11px] text-gray-500 mt-0.5">{nazivKonta(konto)}</p>}
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Pravilo določa</label>
            <select value={vloga} onChange={e => setVloga(e.target.value) /* [PATCH besede-vloga-ui v1] */}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white outline-none focus:border-blue-500">
              <option value="postavka">{jeIzdani ? 'Konto prihodka (postavka)' : 'Konto stroška (postavka)'}</option>
              <option value="saldakonto">{jeIzdani ? 'Konto kupca (celoten račun)' : 'Konto dobavitelja (celoten račun)'}</option>
            </select>
          </div>
          {jeIzdani && vloga !== 'saldakonto' && ( /* [PATCH ir4a-ui-kw v1] vrsta prometa (DDV) -- samo izdana pravila */
            <div>
              <label className="block text-xs text-gray-400 mb-1">Vrsta prometa (DDV)</label>
              <select value={kategorija} onChange={e => setKategorija(e.target.value)}
                className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white outline-none focus:border-blue-500">
                <option value="dom_obdavcen">domači obdavčen</option>
                <option value="eu_oproscen">EU oproščen (46. člen)</option>
                <option value="oproscen">oproščen</option>
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-400 mb-1">Partner (neobvezno)</label>
            <PartnerPickerDok companyId={companyId} initialTax={initial?.partner_tax_number || ''} initialLabel={initial?.partner_naziv || ''}
              onChange={(tax) => setPartnerTax(tax)} />
          </div>
        </div>
        <div className="flex items-center justify-between mt-5">
          <button onClick={izbrisi} className="px-3 py-2 text-red-400 hover:bg-red-950/30 rounded-lg text-sm flex items-center gap-1"><Trash2 className="w-4 h-4" /> Izbriši</button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 text-gray-300 text-sm rounded-lg hover:bg-gray-800">Prekliči</button>
            <button onClick={shrani} disabled={saving} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-sm font-semibold rounded-lg">Shrani</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function KontoBesedePopup({ companyId, konto, onClose, smer = 'prejeti', ddvKategorija = 'dom_obdavcen' }: { companyId: string; konto: string; onClose: () => void; smer?: string; ddvKategorija?: string }) { // [PATCH_OPISI_KONTO_PLUS_V1] export za KontiranjeEditor // [PATCH ir4a-ui-kw v1]
  const [besede, setBesede] = useState<any[]>([]);
  const [konti, setKonti] = useState<any[]>([]);
  const [naziv, setNaziv] = useState('');
  const [nova, setNova] = useState('');
  const [partnerTax, setPartnerTax] = useState('');
  const [kategorija, setKategorija] = useState(ddvKategorija || 'dom_obdavcen'); // [PATCH ir4a-ui-kw v1]
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState<any | null>(null);

  const nalozi = () => {
    setLoading(true);
    api.get(`/sifranti/keywords/${companyId}?smer=${smer}`) // [PATCH ir4a-ui-kw v1]
      .then(r => {
        const zaKonto = (r.data || []).filter((p: any) => p.konto === konto);
        setBesede(zaKonto);
        const n = zaKonto[0]?.konto_naziv; if (n) setNaziv(n);
        setLoading(false);
      })
      .catch(() => { setBesede([]); setLoading(false); });
  };
  useEffect(() => {
    nalozi();
    api.get(`/sifranti/kontni-plan/${companyId}`).then(r => {
      setKonti(r.data || []);
      const k = (r.data || []).find((x: any) => x.code === konto); if (k) setNaziv(k.name);
    }).catch(() => {});
  }, [companyId, konto]);

  const koncniKonti = (konti || []).filter((k: any) => String(k.code || '').length >= 4);

  const dodaj = async () => {
    const b = nova.trim(); if (!b) return;
    try {
      await api.post(`/sifranti/keywords/${companyId}`, { beseda: b, konto, partner_tax_number: (partnerTax || '').trim() || null, smer, ...(smer === 'izdani' ? { ddv_kategorija: kategorija } : {}) }); // [PATCH ir4a-ui-kw v1]
      setNova(''); nalozi();
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Napaka'); }
  };
  const izbrisi = async (id: number) => {
    try { await api.delete(`/sifranti/keywords/${companyId}/${id}`); setBesede(prev => prev.filter(p => p.id !== id)); }
    catch { toast.error('Napaka pri brisanju'); }
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-lg text-white">Opisi za konto</h3>
            <p className="text-sm text-gray-400"><span className="font-mono text-blue-400 font-bold">{konto}</span> {naziv}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-2 mb-4">
          <div className="flex gap-2">
            <input value={nova} onChange={e => setNova(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') dodaj(); }}
              placeholder={smer === 'izdani' ? 'dodaj opis (npr. tisk)…' : 'dodaj opis (npr. elektrika)…'}
              className="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white outline-none focus:border-blue-500" />
            {smer === 'izdani' && ( /* [PATCH ir4a-ui-kw v1] vrsta prometa za nova pravila */
              <select value={kategorija} onChange={e => setKategorija(e.target.value)} title="Vrsta prometa (DDV)"
                className="px-2 py-2 bg-gray-800 border border-gray-700 rounded-lg text-xs text-white outline-none focus:border-blue-500">
                <option value="dom_obdavcen">domači</option>
                <option value="eu_oproscen">EU 46. čl.</option>
                <option value="oproscen">oproščen</option>
              </select>
            )}
            <button onClick={dodaj} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg flex items-center gap-1">
              <Plus className="w-4 h-4" /> Dodaj
            </button>
          </div>
          <PartnerPickerDok companyId={companyId} onChange={(tax) => setPartnerTax(tax)} />
          <p className="text-[11px] text-gray-500">Partner je neobvezen. Klikni kvadratek za urejanje.</p>
        </div>
        {loading ? (
          <div className="text-center py-6 text-gray-500 text-sm">Nalagam…</div>
        ) : besede.length === 0 ? (
          <div className="text-center py-6 text-gray-500 text-sm">Še ni opisov za ta konto.</div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {besede.map((p: any) => {
              const c = _barvaBesedeDok(p.beseda || '');
              return (
                <span key={p.id} onClick={() => setEdit(p)} style={{ backgroundColor: c.bg, borderColor: c.br, color: '#111827' }}
                  className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-lg text-sm font-bold border cursor-pointer hover:brightness-95 select-none">
                  <span>{p.beseda}</span>
                  {p.ddv_kategorija && ( /* [PATCH ir4a-ui-kw v1] */
                    <span className="text-[10px] font-semibold opacity-60 uppercase">{p.ddv_kategorija === 'dom_obdavcen' ? 'dom' : p.ddv_kategorija === 'eu_oproscen' ? 'EU' : 'opr'}</span>
                  )}
                  {p.vloga === 'saldakonto' && ( /* [PATCH besede-vloga-ui v1] */
                    <span className="text-[10px] font-semibold uppercase px-1 rounded bg-black/10" title="Pravilo določa konto partnerja za celoten račun">{p.smer === 'izdani' ? 'kupec' : 'dobav.'}</span>
                  )}
                  {p.izvor === 'rocno' && (
                    <span className="text-[11px] opacity-60" title="Ročno vneseno pravilo — ima prednost pred učnimi vzorci">✎</span>
                  )}
                  {p.partner_tax_number && (
                    <span className="text-xs font-semibold opacity-70" title={p.partner_naziv || p.partner_tax_number}>
                      {p.partner_naziv ? _krajsajDok(p.partner_naziv, 10) : '@' + p.partner_tax_number}
                    </span>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); izbrisi(p.id); }} title="Odstrani" className="opacity-50 hover:opacity-100 leading-none text-base px-0.5">×</button>
                </span>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-gray-500 mt-3">{smer === 'izdani' ? `Te besede usmerijo postavke IZDANIH računov na konto ${konto} (razbitje prihodkov; velja za izbrano vrsto prometa).` : `Te besede usmerijo postavke računa na konto ${konto}, ko sistem prvič vidi dobavitelja (brez učnega vzorca).`}</p> {/* [PATCH ir4a-ui-kw v1] */}
      </div>
      {edit && (
        <BesedaModalDok companyId={companyId} koncniKonti={koncniKonti} initial={edit}
          onClose={() => setEdit(null)} onSaved={() => { setEdit(null); nalozi(); }} />
      )}
    </div>
  );
}

export function KontoSelector({value, companyId, onChange}: {
  value: string;
  companyId: string;
  onChange: (konto: string, naziv: string, requiresCostCenter?: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [q, setQ] = useState('');
  const [konti, setKonti] = useState<any[]>([]);
  const [filtered, setFiltered] = useState<any[]>([]);
  const ref = useRef<HTMLDivElement>(null);
  const qRef = useRef('');

  useEffect(() => {
    if (companyId) {
      api.get(`/sifranti/kontni-plan/${companyId}`).then(r => {
        const analiticki = r.data.filter((k: any) => String(k.code).length >= 4);
        setKonti(r.data);
        setFiltered(analiticki.slice(0, 50));
      }).catch(e => console.error('Konti error:', e));
    }
  }, [companyId]);

  useEffect(() => {
    // [PATCH konto-iskanje-naziv v1] Iscemo SAMO po analiticnih kontih (>=4
    // mesta). Sinteticni konti (npr. "75") ostanejo izven predlogov -- knjizba
    // nanje bi bila racunovodska napaka, ki gre naravnost v VOD/KN-21.
    const analiticki = konti.filter((k: any) => String(k.code).length >= 4);
    // Stevilke -> sifra, crke -> naziv (lib/kontoIskanje). Prazna poizvedba
    // ohrani dosedanjo semantiko: prvih 50 analiticnih kontov.
    if (q.trim()) {
      setFiltered(iskanjeKontov(analiticki, q, 50));
    } else {
      setFiltered(analiticki.slice(0, 50));
    }
  }, [q, konti]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setQ('');
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <div className="relative inline-flex items-center gap-1" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 rounded bg-blue-600/20 hover:bg-blue-600/40 border border-blue-500/40 text-blue-400 font-mono text-xs font-bold transition">
        {value || '???'}<ChevronDown className="w-3 h-3"/>
      </button>
      <button onClick={() => setShowModal(true)} title="Opisi za ta konto"
        className="w-5 h-5 flex items-center justify-center rounded border border-blue-500/40 text-blue-400 hover:bg-blue-600/40 transition flex-shrink-0">
        <Plus className="w-3 h-3"/>
      </button>
      {open && (
        <div className="absolute left-0 top-8 w-80 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-[9999]">
          <div className="p-2 border-b border-gray-800">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-800 rounded-lg">
              <Search className="w-3 h-3 text-gray-400"/>
              <input autoFocus value={q} onChange={e => { setQ(e.target.value); qRef.current = e.target.value; }}
                className="bg-transparent text-xs text-white outline-none flex-1" placeholder="Išči po številki ali nazivu..."/>{/* [PATCH konto-iskanje-naziv v1] */}
              {q && <button onClick={() => setQ('')}><X className="w-3 h-3 text-gray-400"/></button>}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 && <div className="px-3 py-4 text-xs text-gray-500 text-center">Ni rezultatov</div>}
            {filtered.map(k => (
              <button key={k.code} onClick={() => { onChange(k.code, k.name, !!k.requires_cost_center); setOpen(false); setQ(''); }}
                className="w-full text-left px-3 py-2 hover:bg-gray-800 transition flex items-center gap-3">
                <span className="font-mono text-xs text-blue-400 w-16 flex-shrink-0">{k.code}</span>
                <span className="text-xs text-gray-300 truncate">{k.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {showModal && <KontoBesedePopup companyId={companyId} konto={value} onClose={() => setShowModal(false)} />}
    </div>
  );
}
